import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { useReorder } from "./api";

// Phase 4a customer-portal FR-7. Reorder triggers a single POST to
// clone the order into a draft cart, then drops the customer at /cart
// (FR-7 — never auto-confirm; they re-pick dates + re-see pricing).
//
// The `firedRef` guard prevents a double-fire on remount (StrictMode
// double-invokes the effect; navigating away mid-success and coming
// back would also otherwise re-trigger the mutation). When the server
// returns 409 `requires_confirmation` (the customer already had items
// in their draft cart), we surface a confirm-replace prompt and the
// customer re-issues the same call with `replace: true`.
export function ReorderFlowPage(): JSX.Element {
  const { order_number = "" } = useParams<{ order_number: string }>();
  const navigate = useNavigate();
  const reorder = useReorder(order_number);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!order_number || firedRef.current) return;
    firedRef.current = true;
    reorder.mutate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_number]);

  useEffect(() => {
    if (!reorder.isSuccess || !reorder.data) return;
    const timer = setTimeout(() => navigate(reorder.data.redirect_to, { replace: true }), 1200);
    return () => clearTimeout(timer);
  }, [reorder.isSuccess, reorder.data, navigate]);

  const needsConfirm =
    reorder.isError &&
    reorder.error instanceof ApiError &&
    reorder.error.status === 409 &&
    (reorder.error.body as { requires_confirmation?: boolean } | null)?.requires_confirmation === true;

  if (needsConfirm) {
    return (
      <div className="space-y-3 p-4 text-sm" data-testid="reorder-confirm">
        <p>
          You already have items in your cart. Replace them with the items from
          order #{order_number}?
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => reorder.mutate({ replace: true })}
            className="rounded bg-zinc-900 px-4 py-2 text-sm text-white"
            data-testid="reorder-confirm-replace"
          >
            Replace cart
          </button>
          <button
            type="button"
            onClick={() => navigate("/cart")}
            className="rounded border border-zinc-300 px-4 py-2 text-sm"
            data-testid="reorder-keep-cart"
          >
            Keep current cart
          </button>
        </div>
      </div>
    );
  }

  if (reorder.isError) {
    return (
      <div className="p-4 text-sm text-red-700" data-testid="reorder-error">
        Couldn&rsquo;t start the reorder — try from the dashboard.
      </div>
    );
  }

  if (!reorder.isSuccess) {
    return <div className="p-4 text-sm text-zinc-600" data-testid="reorder-loading">Building your cart…</div>;
  }

  return (
    <div className="p-4 text-sm" data-testid="reorder-success">
      <p>
        Added <strong>{reorder.data.cloned_lines}</strong> line
        {reorder.data.cloned_lines === 1 ? "" : "s"} to your cart.
      </p>
      {reorder.data.dropped_lines.length > 0 ? (
        <ul className="mt-2 text-xs text-zinc-600">
          {reorder.data.dropped_lines.map((d, i) => (
            <li key={i}>
              Skipped {d.type_name ?? "(unknown)"} — {d.reason.replaceAll("_", " ")}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="mt-2 text-xs text-zinc-500">Taking you to your cart…</p>
    </div>
  );
}
